<template>
  <oare-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    title="Add Broken Lines"
    :persistent="false"
    :submitLoading="addUndeterminedLinesLoading"
    :submitDisabled="!formComplete"
    @submit="addUndeterminedLines"
  >
    <v-row class="ma-0">Number of Broken Line(s)</v-row>
    <v-row class="ma-0">
      <v-select
        :items="undeterminedOptions"
        v-model="selectedNumber"
        variant="outlined"
        density="compact"
      />
    </v-row>

    <v-row v-if="(selectedNumber || 0) > 2" class="ma-0 mt-4 justify-center">
      <b class="mr-1">NOTE</b>
      Adding more than two broken lines constitutes a discourse break in the
      text. In the event that this insertion is made between two units belonging
      to the same parent (paragraph, clause, etc.), the parent unit will be
      "split" and copied on either side of the insertion. This may result in
      some discourse units having duplicate labels or translations, but this can
      be edited later as needed.
    </v-row>
  </oare-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType } from 'vue';
import { AddUndeterminedLinesPayload, EpigraphicUnitSide } from '@oare/types';
import sl from '@/serviceLocator';
import { TabletRenderer } from '@oare/oare';

export default defineComponent({
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    column: {
      type: Number,
      required: true,
    },
    side: {
      type: String as PropType<EpigraphicUnitSide>,
      required: true,
    },
    previousLineNumber: {
      type: Number,
      required: false,
    },
    textUuid: {
      type: String,
      required: true,
    },
    renderer: {
      type: Object as PropType<TabletRenderer>,
      required: true,
    },
  },
  emits: ['update:modelValue', 'reset-renderer', 'reset-current-edit-action'],
  setup(props, { emit }) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');

    const addUndeterminedLinesLoading = ref(false);

    const undeterminedOptions = ref([1, 2, 3, 4, 5, 6, 7, 8]);
    const selectedNumber = ref<number>();

    const formComplete = computed(() => {
      if (!selectedNumber.value) {
        return false;
      }
      return true;
    });

    const addUndeterminedLines = async () => {
      try {
        addUndeterminedLinesLoading.value = true;

        if (!selectedNumber.value) {
          throw new Error('No number of broken lines selected');
        }

        let previousObjectOnTablet: number | undefined = undefined;
        if (props.previousLineNumber) {
          const unitsOnPreviousLine = props.renderer.getUnitsOnLine(
            props.previousLineNumber
          );
          previousObjectOnTablet =
            unitsOnPreviousLine[unitsOnPreviousLine.length - 1].objOnTablet;
        }

        const payload: AddUndeterminedLinesPayload = {
          type: 'addUndeterminedLines',
          textUuid: props.textUuid,
          side: props.side,
          column: props.column,
          number: selectedNumber.value,
          previousObjectOnTablet,
        };
        await server.editText(payload);
        emit('reset-renderer');
      } catch (err) {
        actions.showErrorSnackbar(
          'Error adding broken lines. Please try again.',
          err as Error
        );
      } finally {
        addUndeterminedLinesLoading.value = false;
        selectedNumber.value = undefined;
        emit('reset-current-edit-action');
        emit('update:modelValue', false);
      }
    };

    return {
      addUndeterminedLinesLoading,
      selectedNumber,
      undeterminedOptions,
      formComplete,
      addUndeterminedLines,
    };
  },
});
</script>
